import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useFrame, useThree } from "@react-three/fiber";
import * as THREE from "three";
import { Interactive } from "../src/interactions/Interactive";

const DEFAULT_HEIGHT = 4;
const DEFAULT_ASPECT = 16 / 9;
const LOOK_LERP_FACTOR = 0.2;

export default function InteractiveVideoPlane({
  id = "interactive-video-plane",
  position = [0, 0, 0],
  height = DEFAULT_HEIGHT,
  radius = 8,
  promptText = "Ver video [E]",
  hudText = "",
  targetRef = null,
  scaleMultiplier = 1,
  videoSrc = "/video/mokey.webm",
  cameraOffset = null,
  cameraTransitionDuration = 1,
}) {
  const meshRef = useRef(null);
  const videoRef = useRef(null);
  const [videoTexture, setVideoTexture] = useState(null);
  const [aspect, setAspect] = useState(DEFAULT_ASPECT);

  const worldPos = useMemo(() => new THREE.Vector3(), []);
  const targetPos = useMemo(() => new THREE.Vector3(), []);
  const lookDirection = useMemo(() => new THREE.Vector3(), []);
  const cameraWorldPos = useMemo(() => new THREE.Vector3(), []);
  const cameraTargetPos = useMemo(() => new THREE.Vector3(), []);

  const camera = useThree((state) => state.camera);

  const derivedCameraPosition = useMemo(() => {
    if (!cameraOffset) return null;
    return [
      position[0] + cameraOffset[0],
      position[1] + cameraOffset[1],
      position[2] + cameraOffset[2],
    ];
  }, [cameraOffset, position]);

  const cameraOverrideConfig = useMemo(() => {
    if (!derivedCameraPosition) return null;
    return {
      position: derivedCameraPosition,
      transitionDuration: cameraTransitionDuration,
    };
  }, [derivedCameraPosition, cameraTransitionDuration]);

  const getCameraTarget = useCallback(() => {
    const mesh = meshRef.current;
    if (!mesh) return null;
    mesh.getWorldPosition(cameraTargetPos);
    return [cameraTargetPos.x, cameraTargetPos.y, cameraTargetPos.z];
  }, [cameraTargetPos]);

  const interactive = useMemo(
    () =>
      new Interactive({
        id,
        promptText,
        hudText,
        radius,
        cameraConfig: cameraOverrideConfig,
        getCameraTarget,
      }),
    [cameraOverrideConfig, getCameraTarget, hudText, id, promptText, radius]
  );

  useEffect(() => () => interactive.dispose(), [interactive]);

  useEffect(() => {
    if (typeof document === "undefined") return undefined;

    const video = document.createElement("video");
    video.src = videoSrc;
    video.crossOrigin = "anonymous";
    video.loop = true;
    video.muted = true;
    video.playsInline = true;
    video.autoplay = true;
    video.preload = "auto";

    const handleLoadedMetadata = () => {
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        setAspect(video.videoWidth / video.videoHeight);
      }
    };

    video.addEventListener("loadedmetadata", handleLoadedMetadata);

    const texture = new THREE.VideoTexture(video);
    texture.minFilter = THREE.LinearFilter;
    texture.magFilter = THREE.LinearFilter;
    texture.generateMipmaps = false;
    if ("colorSpace" in texture) {
      texture.colorSpace = THREE.SRGBColorSpace;
    } else {
      texture.encoding = THREE.sRGBEncoding;
    }

    setVideoTexture(texture);
    videoRef.current = video;

    video.play().catch(() => {
      /* Autoplay can fail without user gesture; ignore */
    });

    return () => {
      video.pause();
      video.removeEventListener("loadedmetadata", handleLoadedMetadata);
      video.src = "";
      video.load();
      texture.dispose();
      setVideoTexture(null);
    };
  }, [videoSrc]);

  useFrame((_, delta) => {
    const mesh = meshRef.current;
    if (!mesh) return;

    mesh.rotation.x = 0;
    mesh.rotation.z = 0;

    mesh.getWorldPosition(worldPos);

    const targetGroup = targetRef?.current?.group;
    if (targetGroup && typeof targetGroup.translation === "function") {
      const translation = targetGroup.translation();
      if (translation) {
        targetPos.set(translation.x, translation.y, translation.z);
        const distance = worldPos.distanceTo(targetPos);
        interactive.setWithinRadius(distance <= interactive.radius, distance);
      } else {
        interactive.setWithinRadius(false);
      }
    } else {
      interactive.setWithinRadius(false);
    }

    if (!camera) return;

    camera.getWorldPosition(cameraWorldPos);

    lookDirection.subVectors(cameraWorldPos, worldPos);
    lookDirection.y = 0;

    if (lookDirection.lengthSq() > 1e-6) {
      lookDirection.normalize();
      const desiredYaw = Math.atan2(lookDirection.x, lookDirection.z);
      const currentYaw = mesh.rotation.y;
      const lerpFactor = 1 - Math.pow(1 - LOOK_LERP_FACTOR, delta * 60);
      mesh.rotation.y = THREE.MathUtils.lerp(currentYaw, desiredYaw, lerpFactor);
    }
  });

  const planeHeight = height;
  const planeWidth = aspect * planeHeight;

  return (
    <mesh
      ref={meshRef}
      position={position}
      scale={[scaleMultiplier, scaleMultiplier, scaleMultiplier]}
      userData={{ name: id }}
    >
      <planeGeometry args={[planeWidth, planeHeight]} />
      <meshBasicMaterial
        map={videoTexture}
        transparent
        side={THREE.DoubleSide}
        toneMapped={false}
        alphaTest={0.01}
        opacity={videoTexture ? 1 : 0}
      />
    </mesh>
  );
}
